import type { MinimalLeadData } from '../types';
import { LEAD_SUBSTATUS_BY_STATUS, getLeadSubstatusLabel, getNormalizedStatusKey, type StatusType } from './statusMapping';

/**
 * Pure selection + grouping rules for the Team pipeline status modal —
 * extracted so the rules are testable without React or the query layer.
 *
 * A card row belongs to the clicked stage when its normalized status matches.
 * Rows within a campaign group by substatus label ONLY when the stage defines
 * substatuses (LEAD_SUBSTATUS_BY_STATUS); statuses without any (enriching)
 * render a flat list.
 */

/** True when the stage's status defines at least one substatus. */
export const stageHasSubstatuses = (stageKey: StatusType): boolean =>
  (LEAD_SUBSTATUS_BY_STATUS[stageKey]?.length ?? 0) > 0;

/** Human label for a row's substatus within the stage, or null when the stage
 *  has no substatus groups. Stray/unknown DB values render through the same
 *  canonical title-casing the rest of the platform uses (getLeadSubstatusLabel)
 *  instead of a bare "Unspecified" — the value itself is more informative. */
export const substatusGroupLabel = (stageKey: StatusType, row: Pick<MinimalLeadData, 'salesSubstatus'>): string | null => {
  const defs = LEAD_SUBSTATUS_BY_STATUS[stageKey];
  if (!defs || defs.length === 0) return null;
  const raw = String(row.salesSubstatus || '').trim();
  if (!raw) return 'No substatus';
  // Known keys → their curated label; unknown keys → canonical title-cased
  // display ("my_custom_state" → "My Custom State"), never "Unspecified".
  return getLeadSubstatusLabel(raw) ?? 'No substatus';
};

/** Rows of the fetched campaign lists that belong to the clicked stage. */
export const selectStageRows = (
  stageKey: StatusType,
  campaignLists: Array<{ campaignId: string; campaignName?: string; leads: MinimalLeadData[] }>,
): Array<{ campaignId: string; campaignName: string; rows: MinimalLeadData[] }> => {
  return campaignLists
    .map(({ campaignId, campaignName, leads }) => ({
      campaignId,
      campaignName: campaignName || campaignId,
      rows: (leads || []).filter((lead) => {
        const normalized = getNormalizedStatusKey(lead.status);
        return normalized === stageKey;
      }),
    }))
    .filter((group) => group.rows.length > 0);
};

/** Group one campaign's stage rows by substatus label (stage order first,
 *  then Unspecified last). Returns [] when the stage has no substatuses. */
export const groupRowsBySubstatus = (
  stageKey: StatusType,
  rows: MinimalLeadData[],
): Array<{ label: string; rows: MinimalLeadData[] }> => {
  if (!stageHasSubstatuses(stageKey)) return [];
  const defs = LEAD_SUBSTATUS_BY_STATUS[stageKey];
  const buckets = new Map<string, MinimalLeadData[]>();
  for (const row of rows) {
    const label = substatusGroupLabel(stageKey, row) ?? 'No substatus';
    const list = buckets.get(label);
    if (list) list.push(row);
    else buckets.set(label, [row]);
  }
  const ordered: Array<{ label: string; rows: MinimalLeadData[] }> = [];
  const emittedLabels = new Set<string>();
  for (const def of defs) {
    // Two defs may share a label (message_ready + ready_to_outreach both
    // render "Ready to Outreach") — emit each LABEL once.
    if (emittedLabels.has(def.label)) continue;
    emittedLabels.add(def.label);
    const found = buckets.get(def.label);
    if (found) ordered.push({ label: def.label, rows: found });
  }
  const noSubstatus = buckets.get('No substatus');
  if (noSubstatus) ordered.push({ label: 'No substatus', rows: noSubstatus });
  // Stray substatuses not in the stage definition keep their canonical
  // title-cased label, after the known ones.
  for (const [label, groupRows] of buckets) {
    if (!emittedLabels.has(label) && label !== 'No substatus') {
      ordered.push({ label, rows: groupRows });
    }
  }
  return ordered;
};
